import Axios from '@/lib/axiosConfig';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/context/Auth';
import { pages } from 'next/dist/build/templates/app-page';
import { IPost } from '@/types';
import { IComment } from '@/components/comments/Comment';

// vote
export const useVotePostsMutation = () => {
  const { getAPIRequestTokenHeaders } = useAuth();
  const queryClient = useQueryClient();

  const voteMutation = useMutation({
    mutationFn: async ({
      vote,
      postId,
    }: {
      vote: 1 | -1 | 0;
      postId: string;
      communityId: string;
    }) => {
      if (vote === 0) {
        return Axios.delete(`/posts/votes`, {
          data: {
            postID: postId,
          },
          headers: getAPIRequestTokenHeaders(),
        });
      } else {
        return Axios.post(
          `/posts/votes`,
          {
            postID: postId,
            value: String(vote),
          },
          {
            headers: getAPIRequestTokenHeaders(),
          }
        );
      }
    },
    onMutate: async ({ postId, vote, communityId }) => {
      // Store previous cache states for rollback
      const previousCaches: Array<[any, any]> = [];

      const updateCache = (queryKey: any[], exact: boolean = false) => {
        // Get and store previous data
        const previousData = queryClient.getQueriesData({ queryKey, exact });
        previousData.forEach(([key, data]) => {
          previousCaches.push([key, data]);
        });

        // Apply optimistic update
        queryClient.setQueriesData(
          { queryKey, exact },
          oldData => {
            if (!oldData) return oldData;
            
            // Handle infinite query structure
            if (oldData.pages && Array.isArray(oldData.pages)) {
              return {
                ...oldData,
                pages: oldData.pages.map((page: any) => {
                  if (page.responseObject && Array.isArray(page.responseObject.rows)) {
                    return {
                      ...page,
                      responseObject: {
                        ...page.responseObject,
                        rows: page.responseObject.rows.map((row: IPost) => {
                          if (row.id === postId) {
                            return {
                              ...row,
                              ...optimisticUpdateVotes({ post: row, vote }),
                            };
                          }
                          return row;
                        }),
                      },
                    };
                  }
                  return page;
                }),
              };
            }
            
            // Handle ForumResponse structure
            if (oldData.responseObject && Array.isArray(oldData.responseObject.rows)) {
              return {
                ...oldData,
                responseObject: {
                  ...oldData.responseObject,
                  rows: oldData.responseObject.rows.map((row: IPost) => {
                    if (row.id === postId) {
                      return {
                        ...row,
                        ...optimisticUpdateVotes({ post: row, vote }),
                      };
                    }
                    return row;
                  }),
                },
              };
            }
            
            // Handle single post structure
            if (oldData.responseObject && oldData.responseObject.id === postId) {
              return {
                ...oldData,
                responseObject: {
                  ...oldData.responseObject,
                  ...optimisticUpdateVotes({ post: oldData.responseObject, vote }),
                },
              };
            }
            
            return oldData;
          }
        );
      };

      // Update all relevant caches
      updateCache(['posts', 'infinite'], false);
      updateCache(['users', 'posts'], false);
      updateCache(['posts', postId], true);
      updateCache(['posts'], false);
      updateCache(['posts/feed'], false);
      updateCache(['posts/my'], false);

      if (communityId) {
        updateCache(['community', communityId, 'posts'], true);
      }

      // Return previous caches for rollback
      return { previousCaches };
    },
    onError: (error: any, variables, context) => {
      // Rollback all cache updates on error
      if (context?.previousCaches) {
        context.previousCaches.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });

  return voteMutation;
};

export function optimisticUpdateVotes({
  post,
  vote,
}: {
  post: IPost | IComment;
  vote: 1 | -1 | 0;
}) {
  const voteData = {};
  //   user sent a remove vote request
  if (vote === 0) {
    // to remove the vote
    // we want to check the prev value of the vote to update it's counter,

    // user cancel upvote
    if (post.vote === 1) {
      voteData.vote = 0;
      voteData.upvoteCount = post.upvoteCount - 1;
    }

    // user cancel the downvote
    if (post.vote === -1) {
      voteData.vote = 0;
      voteData.downvoteCount = post.downvoteCount - 1;
    }
  }

  //   user sent an upvote request
  else if (vote === 1) {
    // want to switch from downvote to upvote
    if (post.vote === -1) {
      voteData.downvoteCount = post.downvoteCount - 1;
    }

    voteData.vote = 1;
    voteData.upvoteCount = post.upvoteCount + 1;
  } else if (vote === -1) {
    // want to switch from upvote to downvote
    if (post.vote === 1) {
      voteData.upvoteCount = post.upvoteCount - 1;
    }

    voteData.vote = -1;
    voteData.downvoteCount = post.downvoteCount + 1;
  }
  return voteData;
}
